import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** Distance from the anchor, and from the viewport edge. */
const GAP_PX = 8;
const MARGIN_PX = 8;

/**
 * An anchor wider than the bubble (a group header row, a full-width cell)
 * centres its hint somewhere the reader is not looking, so those follow the
 * pointer horizontally. A chip or a header is narrower than this and keeps a
 * fixed, centred hint that does not shift as the mouse crosses it.
 */
const WIDE_ANCHOR_PX = 320;

interface Anchored {
  rect: DOMRect;
  text: string;
  pointerX: number | null;
}

interface Placement {
  top: number;
  left: number;
}

/**
 * Every hover hint on these pages, drawn by us instead of by the browser.
 *
 * One listener on the document rather than a wrapper per hint: an element opts
 * in with `data-tip="…"` where it used to carry `title="…"`, which keeps the
 * markup identical (no extra spans inside table cells and flex rows, nothing
 * to get the geometry wrong) and lets a `th`, a `td`, a chip and a button all
 * take a hint the same way. A `\n` in the text breaks the line.
 *
 * The bubble is positioned before it is shown. One that renders where the
 * document puts it and is moved into place afterwards gets painted at the
 * top-left corner first and reads as flying in from (0,0) — so the anchor's
 * rect is taken on the way in, the coordinates are computed in a LAYOUT effect
 * (before the browser paints) once the bubble's own size can be measured, and
 * until they exist the bubble is not visible at all.
 *
 * It closes rather than follows on scroll and resize: the coordinates are
 * viewport coordinates read once, so a bubble that stayed put while the table
 * moved under it would be pointing at a different row.
 */
export function TooltipLayer() {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<Element | null>(null);
  const [anchored, setAnchored] = useState<Anchored | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  useEffect(() => {
    const clear = () => {
      if (!anchorRef.current) return;
      anchorRef.current = null;
      setAnchored(null);
      setPlacement(null);
    };

    const open = (event: Event) => {
      const from = event.target;
      const anchor =
        from instanceof Element ? from.closest("[data-tip]") : null;
      const text = anchor?.getAttribute("data-tip");
      if (!anchor || !text) return clear();
      if (anchor === anchorRef.current) return;
      anchorRef.current = anchor;
      setPlacement(null);
      setAnchored({
        rect: anchor.getBoundingClientRect(),
        text,
        pointerX: "clientX" in event ? (event as MouseEvent).clientX : null,
      });
    };

    // Leaving the window fires no `pointerover` to close the hint on.
    const onOut = (event: PointerEvent) => {
      if (!event.relatedTarget) clear();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") clear();
    };

    document.addEventListener("pointerover", open, true);
    document.addEventListener("pointerout", onOut, true);
    // A click is the act the hint was describing; it has served its purpose.
    document.addEventListener("pointerdown", clear, true);
    document.addEventListener("focusin", open, true);
    document.addEventListener("focusout", clear, true);
    // Capture: the fleet table scrolls in its own container, not on the window.
    window.addEventListener("scroll", clear, true);
    window.addEventListener("resize", clear);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerover", open, true);
      document.removeEventListener("pointerout", onOut, true);
      document.removeEventListener("pointerdown", clear, true);
      document.removeEventListener("focusin", open, true);
      document.removeEventListener("focusout", clear, true);
      window.removeEventListener("scroll", clear, true);
      window.removeEventListener("resize", clear);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  useLayoutEffect(() => {
    if (!anchored || !bubbleRef.current) return;
    const { rect, pointerX } = anchored;
    const { width, height } = bubbleRef.current.getBoundingClientRect();
    const above = rect.top - GAP_PX - height;
    const below = rect.bottom + GAP_PX;
    const clamp = (value: number, limit: number) =>
      Math.min(Math.max(MARGIN_PX, value), Math.max(MARGIN_PX, limit));
    setPlacement({
      // Above by preference: below a hovered row is the next hotel, and the
      // reader is scanning towards it.
      top: clamp(
        above >= MARGIN_PX ? above : below,
        window.innerHeight - height - MARGIN_PX,
      ),
      left: clamp(
        (pointerX != null && rect.width > WIDE_ANCHOR_PX
          ? pointerX
          : rect.left + rect.width / 2) -
          width / 2,
        window.innerWidth - width - MARGIN_PX,
      ),
    });
  }, [anchored]);

  if (!anchored) return null;

  return createPortal(
    <div
      ref={bubbleRef}
      role="tooltip"
      style={{
        top: placement?.top ?? 0,
        left: placement?.left ?? 0,
        visibility: placement ? "visible" : "hidden",
      }}
      // Near-black, deliberately literal: the portal's own `neutral-800` is a
      // mid gray (#525254) that reads as a washed-out panel behind white text.
      className="fixed z-[100] max-w-sm pointer-events-none whitespace-pre-line rounded-md bg-[#1F1F23] px-3 py-2 text-xs leading-snug text-white shadow-lg"
    >
      {anchored.text}
    </div>,
    document.body,
  );
}
